/**
 * Played streak with automatic rest days (docs/MECHANICS.md).
 * A day counts when the player finishes all 3 rounds (any level) on that local date.
 * Every 7 played days in a row bank one rest day (at most MAX_BANK). A gap of missed days
 * is covered if enough rest days are banked; rest days keep the streak but don't add to it.
 * Pure functions over the list of played dates, so the rules are easy to test and change.
 */
import { store } from "./storage";

export const EARN_EVERY = 7;
export const MAX_BANK = 2;
const KEY = "played";
const KEEP = 800;

export interface Streak {
  /** Played days in the current streak (0 if broken). */
  current: number;
  best: number;
  /** Rest days banked right now. */
  bank: number;
  /** Rest days already spent to keep the current streak alive (including pending ones before today). */
  restsUsed: number;
}

const dayNum = (d: string): number => {
  const [y, m, dd] = d.split("-").map(Number);
  return Math.round(Date.UTC(y!, m! - 1, dd!) / 86400000);
};

export function streakOf(played: readonly string[], today: string): Streak {
  const days = [...new Set(played)].filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)).map(dayNum).sort((a, b) => a - b);
  let current = 0;
  let best = 0;
  let bank = 0;
  let restsUsed = 0;
  let last: number | null = null;
  const t = dayNum(today);
  for (const d of days) {
    if (d > t) break;
    const missed = last == null ? 0 : d - last - 1;
    if (last != null && missed <= bank) {
      bank -= missed;
      restsUsed += missed;
      current++;
    } else {
      current = 1;
      bank = 0;
      restsUsed = 0;
    }
    if (current % EARN_EVERY === 0) bank = Math.min(MAX_BANK, bank + 1);
    best = Math.max(best, current);
    last = d;
  }
  if (last == null) return { current: 0, best: 0, bank: 0, restsUsed: 0 };
  // Days missed since the last played day, not counting today (today can still be played).
  const pending = Math.max(0, t - last - 1);
  if (pending > bank) return { current: 0, best, bank: 0, restsUsed: 0 };
  return { current, best, bank: bank - pending, restsUsed: restsUsed + pending };
}

export const getPlayed = (): string[] => store.get<string[]>(KEY) ?? [];

export function recordPlayed(date: string): void {
  const all = getPlayed();
  if (all.includes(date)) return;
  store.set(KEY, [...all, date].sort().slice(-KEEP));
}

export const mergePlayed = (a: readonly string[], b: readonly string[]): string[] => [...new Set([...a, ...b])].sort().slice(-KEEP);
